import { createRouter, createWebHistory } from 'vue-router';
import { getUserFromToken, ensureProjectHydrated, earlyHydration, esSuperAdmin, debeCambiarPassword } from '../utils/auth';
import { validarRutaConAcceso, clearNotificationState, fetchAlertsCustom, prepareAlertsData, safeJsonParse } from '../utils/helpers';
import { verificarAccesoImport } from '../services/importacion';
import { applyProjectLocale } from '../utils/localeBootstrap';
import { enModoProyectoNuevo } from '../stores/proyecto';

// Páginas (lazy-loading: cada ruta descarga su chunk solo cuando se visita)
const Dashboard = () => import('../components/pages/Dashboard.vue');
const Home = () => import('../components/pages/Home.vue');
const Welcome = () => import('../components/pages/Welcome.vue');
const Login = () => import('../components/pages/Login.vue');
const NoAuthorization = () => import('../components/pages/NoAuthorization.vue');
const NotFound = () => import('../components/pages/NotFound.vue');
const PleaseWait = () => import('../components/pages/PleaseWait.vue');

// Detalles
const Details = () => import('../components/Details.vue');
const LogAlerts = () => import('../components/LogAlerts.vue');
const CaudalDetails = () => import('../components/caudalimetro/CaudalDetails.vue');
const VpdDetails = () => import('../components/vpd/DetailsVpd.vue');

// Administración
const Alertas = () => import('../components/administracion/Alerts.vue');
const Usuarios = () => import('../components/administracion/Usuarios.vue');
const Devices = () => import('../components/administracion/Devices.vue');
const Accesos = () => import('../components/administracion/Accesos.vue');
const Proyectos = () => import('../components/administracion/Proyectos.vue');
const Cuarteles = () => import('../components/administracion/Cuarteles.vue');
const Organizaciones = () => import('../components/administracion/Organizaciones.vue');
const CambioClaveAdmin = () => import('../components/administracion/CambioClaveAdmin.vue');
const ImportarDatos = () => import('../components/administracion/ImportarDatos.vue');
// Asistente de Configuración Inicial: orquesta las vistas de Administración que
// ya existen; no reimplementa ninguna de ellas.
const Asistente = () => import('../components/administracion/wizard/AsistenteConfiguracion.vue');

function hasValidMenuSelection() {
  const raw = sessionStorage.getItem('menu');
  if (!raw) return false;
  try {
    const parsed = safeJsonParse(raw, null);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Object.keys(parsed).length > 0;
  } catch {
    return false;
  }
}

function getStoredProjects() {
  const sessionRaw = sessionStorage.getItem('proyectos');
  const localRaw = localStorage.getItem('hb:proyectos');
  const parsed = safeJsonParse(sessionRaw || localRaw || '[]', []);
  return Array.isArray(parsed) ? parsed : [];
}

function hydrateSingleProjectIfPossible() {
  // El asistente está creando un proyecto nuevo: "sin proyecto activo" es el
  // estado correcto y reactivar el anterior aquí lo rompería.
  if (enModoProyectoNuevo()) return false;

  const proyectos = getStoredProjects();
  if (proyectos.length !== 1) return false;

  const proyecto = proyectos[0];
  const menu = proyecto?.menu?.[0];
  if (!proyecto?.id || !menu) return false;

  sessionStorage.setItem('proyectSelected', String(proyecto.id));
  sessionStorage.setItem('proyectSelectedName', proyecto.proyectoName || '');
  sessionStorage.setItem('menu', JSON.stringify(menu));
  sessionStorage.setItem('proyectos', JSON.stringify(proyectos));
  localStorage.setItem('hb:proyectos', JSON.stringify(proyectos));
  localStorage.setItem('hb:last-project', JSON.stringify({
    id: proyecto.id,
    name: proyecto.proyectoName || '',
    menu
  }));
  return true;
}

const routes = [
  { path: '/', redirect: () => {
    // Restaura token compartido (ventana nueva) antes de decidir la ruta
    earlyHydration();
    const token = sessionStorage.getItem('token');
    ensureProjectHydrated();
    // El SuperAdmin entra SIEMPRE por Bienvenida: allí elige entre abrir un
    // proyecto o entrar al Asistente de Configuración Inicial. Autoseleccionarle
    // su único proyecto lo mandaría directo al Home y le escondería esa segunda
    // salida. El resto de perfiles conserva el atajo.
    if (!esSuperAdmin()) {
      hydrateSingleProjectIfPossible();
    }
    const proyectSelected = sessionStorage.getItem('proyectSelected');
    const hasMenu = hasValidMenuSelection();

    // Si no hay token, ir a login
    if (!token) {
      return '/login';
    }
    // Si hay token pero falta proyecto o menú, ir a welcome
    if (!proyectSelected || !hasMenu) {
      return '/welcome';
    }
    // Si hay token + proyecto + menú, ir a home
    return '/home';
  }},
  { path: '/login', component: Login, meta: { rutaProtegida: false } },
  // Recuperacion de contrasena: PUBLICA (sin sesion). Para que funcione hay que
  // marcarla en TRES lugares, no en uno:
  //   1. aca, con `rutaProtegida: false`, para que el guard no exija token;
  //   2. en PUBLIC_PATHS de App.vue, o no se renderiza (no entra por layout,
  //      porque no hay sesion, ni por vista publica);
  //   3. en los dos arrays de mas abajo (boot overlay y validacion), o queda el
  //      overlay #boot colgado encima de la pantalla.
  { path: '/recuperar-clave', component: () => import('../components/pages/RecuperarClave.vue'), meta: { rutaProtegida: false } },
  // Mi cuenta: cambio de la PROPIA contrasena. `requiereProyecto: false` porque
  // un usuario tiene que poder cambiar su clave aunque todavia no tenga proyecto
  // seleccionado — sobre todo si entro con una clave que le fijo un tercero.
  { path: '/mi-cuenta', component: () => import('../components/pages/MiCuenta.vue'), meta: { rutaProtegida: true, requiereProyecto: false } },
  { path: '/welcome', component: Welcome, meta: { rutaProtegida: false } },
  { path: '/dashboard', component: Dashboard, meta: { rutaProtegida: true } },
  { path: '/home', component: Home, meta: { rutaProtegida: true } },

  // Detalles con query params
  { path: '/details', props: (route) => ({ nombre: route.query.nombre }), component: Details, meta: { rutaProtegida: true } },
  { path: '/caudal_details', props: (route) => ({ id: route.query.id }), component: CaudalDetails, meta: { rutaProtegida: true } },
  { path: '/details_vpd', props: (route) => ({ id: route.query.id }), component: VpdDetails, meta: { rutaProtegida: true } },

  // Gestión
  { path: '/alertas', component: Alertas, meta: { rutaProtegida: true } },
  // La vista de Alertas quedó unificada (configuradas + registro de disparos en
  // una sola página con pestañas). `/log_alertas` se conserva como redirección
  // para no romper enlaces/navegación existentes: abre la pestaña de registro.
  { path: '/log_alertas', redirect: '/alertas?tab=log' },
  { path: '/usuarios', component: Usuarios, meta: { rutaProtegida: true } },
  { path: '/dispositivos', component: Devices, meta: { rutaProtegida: true } },
  { path: '/accesos', component: Accesos, meta: { rutaProtegida: true } },
  { path: '/proyectos', component: Proyectos, meta: { rutaProtegida: true } },
  { path: '/organizaciones', component: Organizaciones, meta: { rutaProtegida: true } },
  { path: '/cuarteles', component: Cuarteles, meta: { rutaProtegida: true } },
  // `requiereProyecto: false` porque los dos primeros pasos (organización y
  // proyecto) corren ANTES de que exista un proyecto seleccionado: sin esta
  // marca el guard rebota a /welcome y el asistente nunca llega a mostrarse.
  { path: '/asistente', component: Asistente, meta: { rutaProtegida: true, requiereProyecto: false, requireSuperAdmin: true } },
  { path: '/cambiar-clave', component: CambioClaveAdmin, meta: { rutaProtegida: true, requireSuperAdmin: true } },
  { path: '/importar-datos', component: ImportarDatos, meta: { rutaProtegida: true, requireImportAccess: true } },

  // Páginas especiales
  { path: '/no-authorization', component: NoAuthorization },
  { path: '/no-found', component: NotFound },
  { path: '/please-wait', component: PleaseWait },
  { path: '/:pathMatch(.*)*', redirect: '/no-found' }
];

const router = createRouter({
  history: createWebHistory(),
  routes: routes
});

// ============================================
// LOADING SCREEN
// ============================================
window.showBoot = () => {
  const el = document.getElementById('boot');
  if (el) el.classList.remove('hidden');
};

window.hideBoot = () => {
  const el = document.getElementById('boot');
  if (el) el.classList.add('hidden');
};

function collapseSidebarOnMobile(app) {
  const proxy = app._instance?.proxy;
  if (!proxy) return;
  if (window.matchMedia('(max-width: 768px)').matches) {
    proxy.isCollapsed = true;
    proxy.closeAllDropdowns?.();
  }
}

// ============================================
// ROUTER GUARDS
// ============================================

export function setupRouterGuards(app) {
  router.beforeEach(async (to, from, next) => {
    const pathLower = to.path.toLowerCase();

    // No mostrar boot para welcome, login y errores
    if (!['/no-found', '/no-authorization', '/welcome', '/login', '/recuperar-clave'].includes(pathLower)) {
      window.showBoot();
    }

    // >>> CLAVE FIJADA POR UN TERCERO <<<
    // Va ANTES de `ignorarValidacion` y de la selección de proyecto. Antes
    // estaba más abajo y /welcome —que sale por acá— quedaba fuera del cerco:
    // el usuario reseteado se quedaba ahí operando. /mi-cuenta es la única ruta
    // que se le permite (por eso lleva requiereProyecto: false y renderiza
    // layout aunque no haya proyecto). Se exige sesión: sin token esto no
    // aplica y /login tiene que seguir siendo alcanzable.
    // El backend hace cumplir lo mismo (middlewares/auth.js): esto es la capa
    // de comodidad, no el control.
    // Salidas que NO se bloquean: /login para cerrar sesión y volver a entrar, y
    // /recuperar-clave para quien no recuerda la actual y por lo tanto no puede
    // cambiarla desde Mi cuenta. Sin estas dos, el cerco es una ratonera.
    const SALIDAS_PERMITIDAS = ['/mi-cuenta', '/login', '/recuperar-clave', '/no-authorization', '/no-found'];
    if (sessionStorage.getItem('token') && debeCambiarPassword() && !SALIDAS_PERMITIDAS.includes(pathLower)) {
      return next('/mi-cuenta');
    }

    const ignorarValidacion = ['/no-found', '/no-authorization', '/welcome', '/login', '/recuperar-clave'];
    if (ignorarValidacion.includes(pathLower)) {
      return next();
    }

    // Re-hidratación defensiva (incluye token compartido para ventanas nuevas)
    earlyHydration();
    ensureProjectHydrated();
    hydrateSingleProjectIfPossible();

    // Formato Regional: recién acá el proyecto activo quedó resuelto (token
    // rehidratado + proyecto único autoseleccionado). applyProjectLocale() es
    // idempotente y sale por early-return si el locale no cambió, así que
    // llamarla en cada navegación no tiene costo.
    applyProjectLocale();

    const token = sessionStorage.getItem('token');

    if (to.meta.rutaProtegida) {
      if (!token) {
        return next('/login');
      }

      // Rutas que funcionan sin proyecto seleccionado (hoy: el asistente de
      // configuración inicial, que es justamente donde se crea el primero).
      const requiereProyecto = to.meta.requiereProyecto !== false;

      // Validación con múltiples proyectos
      const proyectos = getStoredProjects();
      const tieneMultiples = Array.isArray(proyectos) && proyectos.length > 1;
      const proyectSelected = sessionStorage.getItem('proyectSelected');
      const hasMenu = hasValidMenuSelection();

      if (requiereProyecto && tieneMultiples && (!proyectSelected || !hasMenu)) {
        const nextUrl = encodeURIComponent(to.fullPath);
        return next(`/welcome?next=${nextUrl}`);
      }

      // Si falta proyecto o menú, obligar selección en welcome
      if (requiereProyecto && (!proyectSelected || !hasMenu)) {
        const nextUrl = encodeURIComponent(to.fullPath);
        return next(`/welcome?next=${nextUrl}`);
      }

      // Si llegamos aquí, hay token y proyecto
      app.config.globalProperties.usuarioActual = getUserFromToken();

      if (to.meta.requireSuperAdmin && !esSuperAdmin()) {
        return next('/no-authorization');
      }

      // Importación manual: super admin O permiso granular (se valida en backend).
      if (to.meta.requireImportAccess && !esSuperAdmin()) {
        const permitido = await verificarAccesoImport();
        if (!permitido) {
          return next('/no-authorization');
        }
      }

      // Reconstruir menú si hace falta
      const menu = sessionStorage.getItem('menu');
      if (menu) {
        const proxy = app._instance?.proxy;
        if (proxy && typeof proxy.loadMenu === 'function') {
          const parsedMenu = safeJsonParse(menu, null);
          if (parsedMenu) {
            proxy.loadMenu(parsedMenu);
          }
        }
      }

      return next();
    }

    return next();
  });

  router.afterEach(() => {
    setTimeout(() => {
      window.hideBoot();
      collapseSidebarOnMobile(app);
    }, 0);
  });
}

export default router;
